'use client'

import React from 'react'
import classNames from 'classnames'
import Link from 'next/link'
import { useTranslations } from 'next-intl'
import { useRouter } from 'next/navigation'
import { Avatar } from '@/ui/atoms/Avatar'
import { CMSLink } from '@/ui/components/CMSLink'
import { Popover, PopoverContent, PopoverTrigger } from '@/ui/atoms/Popover'
import { Profile } from '@/ui/atoms/Profile'
import { Divider } from '@/ui/atoms/Divider'
import { Button } from '@/ui/atoms/Button'
// Supabase auth island (login-system R2): session hydrates client-side after paint, so this
// renders a skeleton first, then the logged-in avatar or the Login button. Static pages stay
// static — this is the only chrome that knows who you are.
import { useSupabaseAuth } from '@/context/supabaseContext'
import { SUPABASE_CONFIGURED } from '@/lib/supabase/client'

type NavAuthProps = {
  className?: string
  isMobile?: boolean
}

export const NavAuth: React.FC<NavAuthProps> = ({ className: classNameFromProps, isMobile }) => {
  const t = useTranslations('header')
  const router = useRouter()
  const { user, isLoaded, signOut } = useSupabaseAuth()

  // No Supabase env (e.g. a build without the login system) — render no auth slots at all.
  if (!SUPABASE_CONFIGURED) return null

  const displayName =
    (user?.user_metadata?.full_name as string | undefined) ||
    (user?.user_metadata?.name as string | undefined) ||
    user?.email ||
    ''
  const initials = displayName
    .split(/\s+/)
    .map((w) => w.charAt(0))
    .slice(0, 2)
    .join('')
    .toUpperCase()

  const profileAvatar = (
    <Avatar
      className={classNames(classNameFromProps)}
      showSkeleton={!isLoaded}
      variant={'circle'}
      size={'medium'}
      initials={initials || undefined}
      userImage={((user?.user_metadata?.avatar_url || user?.user_metadata?.picture) as string | undefined) || undefined}
      showHoverEffect={true}
    />
  )

  const profileMenu = (
    <React.Fragment>
      {/* the identity row itself is the fastest path to the account page */}
      <Link href="/account" className="block no-underline hover:opacity-80">
        <Profile
          image={((user?.user_metadata?.avatar_url || user?.user_metadata?.picture) as string | undefined) || undefined}
          appearance={isMobile ? 'inverted' : 'default'}
          size={'medium'}
          name={displayName}
          mail={user?.email || ''}
        />
      </Link>
      <div className={'flex flex-col justify-center items-start gap-0 mt-3'}>
        <CMSLink
          label={'Dashboard'}
          appearance={isMobile ? 'ghostInverted' : 'ghost'}
          size={'sm'}
          className={'!px-2 w-full justify-start'}
          iconLeft={'dashboard-line'}
          link={{ url: '/dashboard' }}
        />
        <CMSLink
          label={t('accountButtonLabel')}
          appearance={isMobile ? 'ghostInverted' : 'ghost'}
          size={'sm'}
          className={'!px-2 w-full justify-start'}
          iconLeft={'user-3-line'}
          link={{ url: '/account' }}
        />
        <Divider className={'my-3'} inverted={isMobile} />
        <Button
          variant={isMobile ? 'primaryInverted' : 'ghost'}
          size={'md'}
          className={'w-full ' + (isMobile ? '' : '!px-2 justify-start')}
          onClick={async () => {
            await signOut()
            router.refresh()
          }}
        >
          {t('logoutButtonLabel')}
        </Button>
      </div>
    </React.Fragment>
  )

  if (!isLoaded)
    return <Avatar showSkeleton={true} variant={'circle'} size={'medium'} className={'lg:ms-4'} />

  return user ? (
    <React.Fragment>
      {isMobile ? (
        <div className={classNames(classNameFromProps)}>{profileMenu}</div>
      ) : (
        <Popover>
          <PopoverTrigger>{profileAvatar}</PopoverTrigger>
          <PopoverContent>{profileMenu}</PopoverContent>
        </Popover>
      )}
    </React.Fragment>
  ) : (
    <div className={classNames(classNameFromProps, 'flex w-full lg:w-auto flex-row gap-2')}>
      <CMSLink
        label={t('loginButtonLabel')}
        className={'w-full lg:w-auto'}
        appearance={'primaryInverted'}
        size={'md'}
        link={{ url: '/login' }}
      />
    </div>
  )
}
